import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BASEMAPS, { BasemapOption } from './basemaps';

declare global {
  interface Window {
    FulcrumBridgeParams?: {
      assets?: {
        [key: string]: string;
      };
    };
  }
}

const ORDERED_IDS = [
  'google-roadmap',
  'google-terrain',
  'google-satellite',
  'google-hybrid',
  'roadmap',
  'terrain',
  'hybrid',
  'osm',
  'none',
];

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ selected }) => ({
  position: 'relative',
  borderRadius: 16,
  boxShadow: 'none',
  height: 100,
  width: '100%',
  overflow: 'hidden',
  backgroundColor: '#EEEEEE',
  '&:after': {
    border: selected ? '3px solid #4BAEF8' : 'none',
    borderRadius: 16,
    content: '""',
    display: 'block',
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    pointerEvents: 'none',
  },
}));

const LabelOverlay = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'rgba(61, 64, 67, 0.8)',
  color: '#fff',
  padding: '6px 4px',
  textAlign: 'center',
  fontSize: '13px',
  lineHeight: 1.2,
});

interface BasemapPickerProps {
  activeBasemapId: string | null;
  assets?: { [key: string]: string };
  onChange: (basemap: BasemapOption) => void;
}

const BasemapPicker: React.FC<BasemapPickerProps> = ({ activeBasemapId, assets, onChange }) => {
  const orderedBasemaps = ORDERED_IDS.map((id) => BASEMAPS.find((b) => b.id === id)).filter(Boolean) as BasemapOption[];

  return (
    <Box sx={{ p: 2, width: '100%', overflowX: 'auto' }}>
      <Grid container spacing={1.5}>
        {orderedBasemaps.map((basemap) => {
          const isSelected = activeBasemapId === basemap.id || (activeBasemapId === null && basemap.id === 'none');
          const displayName = basemap.esriMapsDisplayName || basemap.name;
          const thumbnailUrl = assets ? assets[basemap.esriMapsThumbnailUrl || ''] : '';

          return (
            <Grid
              item
              key={basemap.id}
              sx={{
                width: '20%',
                flexBasis: '20%',
                flexGrow: 0,
                maxWidth: '20%',
                minWidth: 120,
              }}
            >
              <StyledCard selected={isSelected} data-selected={isSelected}>
                <CardActionArea
                  onClick={() => onChange(basemap)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'flex-start',
                  }}
                >
                  {thumbnailUrl ? (
                    <CardMedia
                      component="img"
                      height="100%"
                      image={thumbnailUrl}
                      alt={displayName}
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Box />
                  )}
                  <LabelOverlay>
                    {displayName}
                  </LabelOverlay>
                </CardActionArea>
              </StyledCard>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default BasemapPicker;
